// Port of ios/Sources/ViewModels/SearchResultsPresentation.swift (SearchResultsPresentation +
// RecentSearches).
//
// A search result list as the screen draws it (shared/API.md §23) — pure, so the ordering,
// the snippet window and every highlight offset are testable without a database.
//
// Rows are references, not messages, on the same rule the pinned board follows: who said it,
// where, when, and enough of it to recognise, with the message itself one tap away.
//
// OFFSETS: the Swift ranges count Characters; here they count UTF-16 code units — the unit a
// browser's string slicing (and so the row's highlight drawing) speaks. `SearchQuery.fold` keeps
// UTF-16 length on the web for exactly this reason.
import type { ChannelRecord, PostRecord, UserRecord } from './records';
import * as SearchQuery from './searchQuery';

/** A half-open range [start, end) of UTF-16 offsets — Swift's `Range<Int>`. */
export interface TextRange {
  start: number;
  end: number;
}

/**
 * `highlights` are ranges in `message` (the WINDOWED string, not the post's raw text) covering
 * the query's terms.
 */
export interface Row {
  postId: string;
  channelId: string;
  channelName: string;
  authorName: string;
  timeLabel: string;
  message: string;
  highlights: TextRange[];
}

/**
 * DMs and group messages belong to NO team on the wire, and the desktop shows them in
 * every team's results — filtering them out because their `team_id` is empty would hide
 * exactly the messages people search for most.
 */
export function isInTeamScope(channel: Pick<ChannelRecord, 'type' | 'teamId'>, teamId: string): boolean {
  return channel.type === 'D' || channel.type === 'G' || channel.teamId === '' || channel.teamId === teamId;
}

/**
 * The pieces `rows` borrows from modules ported by other families, injected so this file does
 * not import files that may not exist yet:
 * - `flattenForOneLine` = `ChannelListDerivation.flattenForOneLine` (the one-line flattening every
 *   list surface uses; `messagePreview` with no author prefix is that plus the attachment
 *   placeholder, which is applied here);
 * - `userDisplayName` = `ChannelListDerivation.userDisplayName` (→ `TeammateNameDisplay`);
 * - `relativeTime` = `RelativeTime.format(epochMillis:now:labels:timeZone:)`.
 */
export interface RowsDeps {
  flattenForOneLine: (message: string) => string;
  userDisplayName: (user: UserRecord) => string;
  relativeTime: (epochMillis: number, now: number, labels: { yesterday: string }, timeZone?: string) => string;
}

export interface RowsInput {
  /**
   * In the order they should appear — the server's `order`, or the local index's newest-first.
   * Never re-sorted here, so the list and whoever answered it cannot disagree.
   */
  posts: readonly PostRecord[];
  channelNames: Readonly<Record<string, string>>;
  usersById: Readonly<Record<string, UserRecord>>;
  query: SearchQuery.Parsed;
  now: number;
  yesterdayLabel: string;
  attachmentPlaceholder: string;
  unknownAuthorLabel: string;
  unknownChannelLabel: string;
  timeZone?: string;
}

export function rows(input: RowsInput, deps: RowsDeps): Row[] {
  return input.posts.map((post) => {
    // The same flattening every other list surface uses: Markdown that reads as its own
    // source is noise, and a pasted stack trace must not decide how tall this row is.
    const flattenedText = deps.flattenForOneLine(post.message);
    const flattened = flattenedText === '' ? input.attachmentPlaceholder : flattenedText;
    const windowed = snippet(flattened, input.query);
    const author = input.usersById[post.userId];
    return {
      postId: post.id,
      channelId: post.channelId,
      channelName: input.channelNames[post.channelId] ?? input.unknownChannelLabel,
      authorName: author ? deps.userDisplayName(author) : input.unknownAuthorLabel,
      timeLabel: deps.relativeTime(post.createAt, input.now, { yesterday: input.yesterdayLabel }, input.timeZone),
      message: windowed.text,
      highlights: windowed.highlights,
    };
  });
}

export interface Snippet {
  text: string;
  highlights: TextRange[];
}

/**
 * Where `query`'s terms appear in `text` — case-insensitive, non-overlapping, in order.
 * Folded character by character (`SearchQuery.fold`) rather than `toLowerCase()`, because
 * that can change a string's LENGTH and every offset here is applied to the unfolded
 * original. Prefix matching means a search for "reun" highlights only those four
 * characters of "reunión" — the truth about what matched.
 */
export function highlights(text: string, query: SearchQuery.Parsed): TextRange[] {
  const needles = [...query.phrases, ...query.words].filter((n) => n.trim() !== '');
  if (needles.length === 0) return [];
  const haystack = SearchQuery.fold(text);
  const found: TextRange[] = [];
  for (const needle of needles) {
    const folded = SearchQuery.fold(needle);
    if (folded === '' || folded.length > haystack.length) continue;
    let index = haystack.indexOf(folded, 0);
    while (index >= 0) {
      found.push({ start: index, end: index + folded.length });
      index = haystack.indexOf(folded, index + folded.length);
    }
  }
  return merge(found);
}

function isLowSurrogate(text: string, index: number): boolean {
  const code = text.charCodeAt(index);
  return code >= 0xdc00 && code <= 0xdfff;
}

/**
 * The part of `text` worth showing, with the highlights re-based onto it. The window
 * opens `lead` characters before the FIRST match rather than at character zero, with an
 * ellipsis saying so — a result row that shows the first three lines of a document nobody
 * was looking for is indistinguishable from a wrong result.
 */
export function snippet(text: string, query: SearchQuery.Parsed): Snippet {
  const found = highlights(text, query);
  if (text.length <= window) return { text, highlights: found };

  const firstMatch = found[0]?.start ?? 0;
  let start = Math.max(firstMatch - lead, 0);
  // Back up to a space so the snippet does not open mid-word; give up after a short look
  // rather than walking to the start of a paragraph.
  if (start > 0) {
    const floor = Math.max(start - wordBoundaryLookback, 0);
    let cursor = start;
    while (cursor > floor && !/\s/u.test(text[cursor - 1])) cursor -= 1;
    if (cursor > floor) start = cursor;
  }
  // Web-only: offsets are UTF-16 units here, and a window edge must not split an emoji's
  // surrogate pair (Swift counts whole Characters and cannot).
  if (start > 0 && isLowSurrogate(text, start)) start -= 1;
  let end = Math.min(start + window, text.length);
  if (end < text.length && isLowSurrogate(text, end)) end -= 1;
  const prefix = start > 0 ? ellipsis : '';
  const suffix = end < text.length ? ellipsis : '';
  const body = text.slice(start, end);

  const shift = prefix.length - start;
  const rebased: TextRange[] = [];
  for (const range of found) {
    // Only ranges wholly inside the window survive: half a highlight hanging off the
    // ellipsis points at a word the row is not showing.
    if (range.start >= start && range.end <= end) {
      rebased.push({ start: range.start + shift, end: range.end + shift });
    }
  }
  return { text: prefix + body + suffix, highlights: rebased };
}

function merge(ranges: TextRange[]): TextRange[] {
  if (ranges.length < 2) return ranges;
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: TextRange[] = [];
  let current = sorted[0];
  for (const range of sorted.slice(1)) {
    if (range.start <= current.end) {
      // Touching spans merge as well as overlapping ones: drawn separately they are
      // one continuous highlight anyway.
      current = { start: current.start, end: Math.max(current.end, range.end) };
    } else {
      merged.push(current);
      current = range;
    }
  }
  merged.push(current);
  return merged;
}

const ellipsis = '…';
/**
 * About three lines at the row's type size — applied here so the highlight offsets are
 * measured against what is drawn.
 */
const window = 240;
const lead = 40;
const wordBoundaryLookback = 16;

/**
 * The list under an empty search box — the desktop's own "recent searches" (shared/API.md
 * §23). Kept per TEAM in `sync_state` — wiped with the account, never leaves the device
 * (Mattermost has no server-side search history). One query per line: a single-line field's
 * value cannot contain a newline, so there is no escaping scheme to corrupt.
 */
export const RecentSearches = {
  /** Enough to be useful, short enough that the list never pushes the box off the screen. */
  limit: 8,

  key(teamId: string): string {
    return 'recent_searches_' + teamId;
  },

  /**
   * `query` at the top, everything else after it, capped. The dedupe is case-insensitive
   * but keeps the spelling JUST typed. A blank query is not remembered at all.
   */
  add(existing: readonly string[], query: string): string[] {
    const trimmed = trimSpaces(query);
    if (trimmed === '') return [...existing];
    const folded = SearchQuery.fold(trimmed);
    const kept = existing.filter((q) => SearchQuery.fold(trimSpaces(q)) !== folded);
    return [trimmed, ...kept].slice(0, RecentSearches.limit);
  },

  remove(existing: readonly string[], query: string): string[] {
    const folded = SearchQuery.fold(trimSpaces(query));
    return existing.filter((q) => SearchQuery.fold(trimSpaces(q)) !== folded);
  },

  /** Tolerant on purpose: a half-written value degrades to the queries still readable. */
  decode(raw: string | null | undefined): string[] {
    if (raw == null) return [];
    return raw
      .split('\n')
      .map(trimSpaces)
      .filter((q) => q !== '')
      .slice(0, RecentSearches.limit);
  },

  encode(queries: readonly string[]): string {
    return queries.slice(0, RecentSearches.limit).join('\n');
  },
};

/** Swift's `trimmingCharacters(in: .whitespaces)`: spaces and tabs, not newlines. */
function trimSpaces(text: string): string {
  return text.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
}
